import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import Modal from '@/components/ui/Modal';
import Price from '@/components/ui/Price';
import { selectWaiterAuth } from './waiterSlice';
import { useIsWaiterSurface } from './waiterSurface';
import { useWaiterOrderStream } from './useWaiterOrderStream';
import { useAdaptivePolling, useObservedPollResponse } from './useAdaptivePolling';
import RoundActionConfirmDialog from './RoundActionConfirmDialog';
import PendingRoundsSheet from './PendingRoundsSheet';
import { pendingActionErrorKey, useTakeOverRound } from './useTakeOverRound';
import {
  waiterApi,
  useWaiterTablesQuery,
  useValidateOrderMutation,
  useRefuseOrderMutation,
  type PendingValidationRound,
  type WaiterTable,
} from './waiterApi';

/** DF-0223 — delai au bout duquel « Plus tard » rend la main : une tournee
 *  toujours en attente redemande a etre traitee. */
const PENDING_REMINDER_MS = 60_000;

interface PendingRow {
  table: WaiterTable;
  round: PendingValidationRound;
}

function priceToCents(value: string): number {
  return Math.round(parseFloat(value) * 100);
}

/**
 * SC-684 — le popper « commande QR en attente » doit atteindre le serveur quelle
 * que soit la page qu'il regarde. Avant, seule la grille des tables ouvrait le
 * flux SSE : entrer dans une table pour prendre une commande démontait
 * TablesOverview, coupait le flux, et les commandes des AUTRES tables ne
 * signalaient plus rien. Ce composant est monté au niveau de la route
 * /:restaurantUuid — donc au-dessus de la grille ET de l'écran de prise de
 * commande — et porte désormais le flux à lui seul.
 *
 * SC-692 — « monté au-dessus de tout » ne veut pas dire « visible partout » :
 * l'écran /table/:token sert AUSSI le client qui a scanné le QR. Le veilleur
 * exige donc une surface du personnel (useIsWaiterSurface) EN PLUS d'un serveur
 * déverrouillé pour ce restaurant. Sur le parcours client il ne rend rien et
 * n'interroge même pas l'endpoint de salle — y compris quand le jeton serveur
 * traîne encore sur l'appareil.
 *
 * SC-715 — Refuser passe par une deuxième confirmation, parce qu'il ne se
 * rattrape pas. SC-953 — Valider aussi (RoundActionConfirmDialog) ; Modifier
 * vit dans la feuille de détail, là où le serveur voit les plats.
 */
export default function WaiterPendingOrdersHost() {
  const { restaurantUuid = '' } = useParams<{ restaurantUuid: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const auth = useAppSelector(selectWaiterAuth);
  const isWaiterSurface = useIsWaiterSurface(restaurantUuid);

  const active =
    isWaiterSurface && !!auth && !auth.uiLocked && auth.restaurantUuid === restaurantUuid;
  const token = active && auth ? auth.token : '';

  // DF-0153 — le FLUX ne suit pas la route, contrairement au sondage ci-dessous.
  // `isWaiterSurface` vaut faux dès que le serveur quitte la grille des tables
  // pour composer un ticket sur la carte : le flux était coupé puis rouvert à
  // chaque aller-retour. Mesuré en prod le 2026-09-04 : 295 (re)connexions en
  // 24 h pour 13 restaurants, dont 231 de moins de 10 s ; 182 pour le seul Yuji.
  // Chacune rejoue la fenêtre de 12 h puis invalide 4 tags. L'hôte est monté
  // au-dessus du routeur (`App.tsx`), donc rien n'obligeait à couper entre deux
  // écrans du même service. Restent seules raisons de couper : le verrou
  // d'inactivité, l'onglet caché, et l'absence d'authentification serveur.
  const streamActive =
    !!auth && !auth.uiLocked && auth.restaurantUuid === restaurantUuid;
  const streamToken = streamActive && auth ? auth.token : '';

  const [isVisible, setIsVisible] = useState(!document.hidden);
  useEffect(() => {
    const handler = () => setIsVisible(!document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Same args as TablesOverview's poll → RTK Query serves both from one cache
  // entry, so being mounted on both screens costs no extra request.
  // DF-0177 — meme recul que les autres sondages serveur. Cache partage avec
  // `TablesOverview` : RTK Query retient le plus court des deux intervalles,
  // et les deux reculent ensemble puisqu'ils lisent la meme reponse.
  const [tablesPollMs, observeTables] = useAdaptivePolling(isVisible, 10_000, 60_000);
  const {
    data: tables,
    refetch,
    fulfilledTimeStamp: tablesStamp,
  } = useWaiterTablesQuery(
    { restaurantUuid, token },
    { pollingInterval: tablesPollMs, skip: !active, refetchOnMountOrArgChange: true },
  );
  useObservedPollResponse(observeTables, tables, tablesStamp);

  // Spec §1.3 G4 / 05-regression-risks.md F-1 — this is now the ONE waiter SSE
  // stream (F2 removes the second instance `NotesListPage` used to open). Any
  // relevant event invalidates the waiter-mode RTK Query tags so every
  // subscribed query — this host's own `tables/`, `NotesListPage`'s `tables/`
  // + `takeaway/`, and the open ticket's `sessions/<uuid>/` — refetches on its
  // own, without each screen owning a stream instance.
  // 2026-08-28 waiter-salle-ux spec §4 — `OnlineOrders` joins the invalidation
  // set so the Emporter « En ligne » list/detail pick up `order_updated` etc.
  // without their own poll waiting it out.
  useWaiterOrderStream({
    restaurantUuid,
    token: streamToken,
    enabled: streamActive && isVisible,
    onOrdersChanged: () => {
      dispatch(waiterApi.util.invalidateTags(['WaiterTables', 'Takeaway', 'SessionDetail', 'OnlineOrders']));
    },
  });

  const [validateOrder] = useValidateOrderMutation();
  const [refuseOrder] = useRefuseOrderMutation();
  const takeOverRound = useTakeOverRound(restaurantUuid, token);
  const [dismissedKey, setDismissedKey] = useState('');
  const [busyOrderUuid, setBusyOrderUuid] = useState<string | null>(null);
  // Lequel des deux gestes est parti : griser les deux boutons ne dit pas si
  // l'appui a été pris, et sur un réseau de salle l'attente se compte en
  // secondes. Le bouton pressé dit « Envoi… », l'autre garde son nom.
  const [busyAction, setBusyAction] = useState<'validate' | 'refuse' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // SC-715 / SC-953 — le geste qui attend sa confirmation. Rien n'est envoyé
  // tant qu'il est posé : c'est le second clic qui valide ou refuse.
  const [confirmTarget, setConfirmTarget] = useState<
    (PendingRow & { action: 'validate' | 'refuse' }) | null
  >(null);
  // La table dont le serveur a demandé le détail — la file reste ouverte
  // derrière, il revient dessus en refermant la feuille.
  const [detailTableUuid, setDetailTableUuid] = useState<string | null>(null);

  const rows = useMemo<PendingRow[]>(
    () =>
      // SC-976 design §5.1/E-20 — a member row carries the SAME session (and
      // so the same `pendingValidation`) as its anchor: without this filter
      // every waiting round would surface twice, once per table, with a
      // different `table.number` each time.
      (tables ?? [])
        .filter((table) => table.role !== 'member')
        .flatMap((table) => (table.session?.pendingValidation ?? []).map((round) => ({ table, round }))),
    [tables],
  );
  const pendingKey = useMemo(
    () =>
      rows
        .map((row) => row.round.orderUuid)
        .sort()
        .join('|'),
    [rows],
  );

  // Clearing the dismissal once the queue empties is what makes the NEXT round
  // pop again after the waiter tapped "Plus tard" on this one.
  useEffect(() => {
    if (!pendingKey) setDismissedKey('');
  }, [pendingKey]);

  // DF-0223 — « Plus tard » ne voulait pas dire « jamais » : tant que la MEME
  // ronde reste en attente, rien ne la rappelait, et la file ne revenait qu'au
  // rechargement de la page. Une tournee de client qui dort n'est pas un
  // rappel qu'on peut se permettre de perdre : elle revient au bout d'une
  // minute, aussi longtemps qu'elle n'est ni validee ni refusee.
  useEffect(() => {
    if (!dismissedKey || dismissedKey !== pendingKey) return;
    const timer = window.setTimeout(() => setDismissedKey(''), PENDING_REMINDER_MS);
    return () => window.clearTimeout(timer);
  }, [dismissedKey, pendingKey]);

  const detailTable = useMemo(
    () => rows.find((row) => row.table.uuid === detailTableUuid)?.table ?? null,
    [rows, detailTableUuid],
  );
  const detailRounds = useMemo(
    () => rows.filter((row) => row.table.uuid === detailTableUuid).map((row) => row.round),
    [rows, detailTableUuid],
  );

  // La dernière ronde de la table vient d'être validée ou refusée : la feuille
  // n'a plus rien à montrer, elle se retire et rend la file au serveur.
  useEffect(() => {
    if (detailTableUuid && detailRounds.length === 0) setDetailTableUuid(null);
  }, [detailTableUuid, detailRounds.length]);

  const open = active && rows.length > 0 && pendingKey !== dismissedKey;

  const runAction = async (round: PendingValidationRound, action: 'validate' | 'refuse') => {
    setBusyOrderUuid(round.orderUuid);
    setBusyAction(action);
    setError(null);
    try {
      const mutation = action === 'validate' ? validateOrder : refuseOrder;
      await mutation({ orderUuid: round.orderUuid, roundNumber: round.roundNumber, token }).unwrap();
      void refetch();
    } catch (err) {
      // SC-953 — un collègue est passé avant : le dire, et relire la file
      // pour que la ronde disparaisse de cet écran aussi.
      setError(t(pendingActionErrorKey(err)));
      void refetch();
    } finally {
      setBusyOrderUuid(null);
      setBusyAction(null);
    }
  };

  // SC-953 — « Modifier » (depuis la feuille de détail) : en cas de succès le
  // ticket s'ouvre sur le panier du serveur ; la ronde quitte la file au
  // prochain sondage, comme après Valider.
  const runModify = async (round: PendingValidationRound) => {
    setBusyOrderUuid(round.orderUuid);
    setError(null);
    try {
      await takeOverRound(round, detailTable?.session?.mode === 'buffet');
      setDetailTableUuid(null);
      void refetch();
    } catch (err) {
      setError(t(pendingActionErrorKey(err)));
      void refetch();
    } finally {
      setBusyOrderUuid(null);
    }
  };

  const confirmAction = () => {
    const target = confirmTarget;
    setConfirmTarget(null);
    if (target) void runAction(target.round, target.action);
  };

  if (!open) return null;

  return (
    <>
      <Modal
        open
        // Tant que la confirmation est levée, ni Échap ni le fond ne referment
        // la file derrière elle : le serveur perdrait de vue ce qu'il est en
        // train de valider ou de refuser.
        onClose={() => {
          if (!confirmTarget && !detailTableUuid) setDismissedKey(pendingKey);
        }}
        // DF-0223 — un appui n'importe ou a cote refermait l'alerte exactement
        // comme « Plus tard », et plus rien ne la rappelait tant que la file
        // ne changeait pas : le serveur perdait la seule entree visible vers
        // Valider. Seul « Plus tard » (et Echap) la referme desormais.
        dismissOnBackdrop={false}
        // La feuille de détail et la confirmation posent leur propre voile
        // par-dessus : celui-ci s'efface plutôt que d'empiler un second flou
        // plein écran. La file elle-même reste rendue derrière.
        scrim={!detailTableUuid && !confirmTarget}
        ariaLabel={t('waiter.pendingAlert.title')}
      >
        {/* La fenêtre est bornée à la hauteur de l'écran par `Modal` : c'est
            cette chaîne flex qui décide QUI, dedans, prend le débordement.
            Sans elle, entête, tables et pied s'empilaient au naturel, et un
            service qui envoie huit tables d'un coup poussait « Plus tard » et
            les dernières tables sous le bord de l'écran, hors d'atteinte —
            l'entête et le pied ne bougent plus, la liste seule défile. */}
        {/* `waiter-surface` : le veilleur est monté à côté de `<Outlet/>`
            (App.tsx), donc HORS du `.waiter-root` qui porte la remise à zéro du
            carnet. Sans elle, le titre et chaque ligne de résumé gardaient les
            marges par défaut du navigateur — mesuré au banc : 36 px perdus dans
            l'entête et 64 px par table, soit la moitié d'une ligne de plus par
            table — et la liste de plats de la feuille sortait à puces. */}
        <div
          data-testid="waiter-pending-alert"
          className="waiter-surface flex flex-col min-h-0 flex-1"
        >
          {/* « Plus tard » vit dans l'entête, comme la croix de la feuille : en
              pied il coûtait 54 px de bandeau pour un seul bouton — 21 % de la
              fenêtre sur un 320×568 — alors qu'ici il ne coûte rien et gagne au
              passage une cible de 44 px. */}
          <div className="shrink-0 px-4 py-2 border-b-2 border-ink flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="font-display text-base truncate">
                {t('waiter.pendingAlert.title')}
              </h2>
              <p className="font-mono text-xs text-ink/70">
                {t('waiter.pendingAlert.subtitle', { count: rows.length })}
              </p>
            </div>
            <button
              type="button"
              data-testid="waiter-pending-later"
              onClick={() => setDismissedKey(pendingKey)}
              className="shrink-0 min-h-[44px] bg-paper border-2 border-ink rounded-btn-sm px-3 py-2.5 font-display text-sm"
            >
              {t('waiter.pendingAlert.later')}
            </button>
          </div>
          <div
            data-testid="waiter-pending-scroll"
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-2 space-y-2"
          >
            {rows.map(({ table, round }) => {
              const busy = busyOrderUuid === round.orderUuid;
              // SC-976 D2 — l'étiquette de tablée remplace le numéro partout
              // où un humain la lit ; une session simple garde son numéro nu.
              const label = table.session?.tableLabel ?? table.number;
              return (
                <div
                  key={round.orderUuid}
                  data-testid={`waiter-pending-row-${round.orderUuid}`}
                  className="border-2 border-ink rounded-btn-sm p-2 flex flex-col items-stretch gap-2 bg-cream min-[360px]:flex-row min-[360px]:items-center min-[360px]:justify-between min-[360px]:gap-3"
                >
                  {/* Le serveur ne valide pas trois lignes de résumé les yeux
                      fermés : la table s'ouvre sur ses plats. */}
                  <button
                    type="button"
                    data-testid={`waiter-pending-detail-${round.orderUuid}`}
                    onClick={() => setDetailTableUuid(table.uuid)}
                    className="min-w-0 text-start"
                    aria-label={t('waiter.pendingAlert.openDetail', { number: label })}
                  >
                    <p className="font-display text-base truncate">
                      {t('waiter.pendingAlert.tableLabel', { number: label })}
                    </p>
                    {/* `whitespace-nowrap` : sans lui « Ronde 2 · 14 articles ·
                        18,90 € » se pliait en six lignes sur un 320 px et la
                        ligne montait à 158 px — trois tables visibles sur
                        vingt. Il déborde plutôt qu'il ne plie ; le nom de la
                        table, lui, reste lisible au-dessus. */}
                    <p className="font-mono text-xs text-ink/70 truncate whitespace-nowrap">
                      {t('waiter.pendingSheet.roundLabel', { n: round.roundNumber })} ·{' '}
                      {t('waiter.pendingSheet.itemsCount', { count: round.itemsCount })} ·{' '}
                      <Price amountCents={priceToCents(round.total)} />
                    </p>
                  </button>
                  {/* Sous 360 px les deux boutons occupaient 182 px des 240 px
                      de la ligne : il ne restait que 39 px au numéro de table,
                      qui sortait « Ta… ». Ils passent sous le titre. */}
                  <div className="grid grid-cols-2 gap-2 min-[360px]:flex min-[360px]:shrink-0">
                    <button
                      type="button"
                      disabled={busy}
                      data-testid={`waiter-pending-refuse-${round.orderUuid}`}
                      onClick={() => setConfirmTarget({ table, round, action: 'refuse' })}
                      className="min-h-[44px] bg-paper border-2 border-ink rounded-btn-sm px-3 py-2.5 font-display text-sm disabled:opacity-50"
                    >
                      {busy && busyAction === 'refuse'
                        ? t('waiter.notes.online.actionPending')
                        : t('waiter.pendingSheet.refuse')}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      data-testid={`waiter-pending-validate-${round.orderUuid}`}
                      onClick={() => setConfirmTarget({ table, round, action: 'validate' })}
                      className="min-h-[44px] bg-mint border-2 border-ink rounded-btn-sm px-3 py-2.5 font-display text-sm disabled:opacity-50"
                    >
                      {busy && busyAction === 'validate'
                        ? t('waiter.notes.online.actionPending')
                        : t('waiter.pendingSheet.validate')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {error && (
            <p
              className="shrink-0 border-t-2 border-ink px-4 py-1 font-mono text-sm text-red-600"
              role="alert"
            >
              {error}
            </p>
          )}
        </div>
      </Modal>
      <PendingRoundsSheet
        open={detailTable !== null}
        onClose={() => setDetailTableUuid(null)}
        tableNumber={detailTable?.session?.tableLabel ?? detailTable?.number ?? ''}
        rounds={detailRounds}
        onValidate={(round) => void runAction(round, 'validate')}
        onRefuse={(round) => void runAction(round, 'refuse')}
        onModify={(round) => void runModify(round)}
        busyOrderUuid={busyOrderUuid}
        error={error}
      />
      <RoundActionConfirmDialog
        action={confirmTarget?.action ?? null}
        roundNumber={confirmTarget?.round.roundNumber ?? null}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={confirmAction}
        busy={busyOrderUuid !== null}
      />
    </>
  );
}
